import React, { useState, useEffect } from 'react';
import {
  BarChart3, Building, Upload, FileText, Brain, Search, Loader2,
  Trash2, LogOut, User, Database, History,
  AlertCircle, CheckCircle, XCircle, ChevronDown,
  Filter, SortAsc, SortDesc, X, Eye, Maximize2,
  Clock, ChevronUp, MessageSquare, Calendar, AlertTriangle,
  Folder, FolderOpen, CreditCard, Download
} from 'lucide-react';
import { api } from '../services/api';
import UserAccountDropdown from './UserAccountDropdown';
import AnalyticsDashboard from './AnalyticsDashboard';
import UserManagement from './UserManagement';
import CompanyManagement from './CompanyManagement';
import SubscriptionManagement from './SubscriptionManagement';
import BackfillOriginals from './BackfillOriginals';
import UsageWidget from './UsageWidget';
import FolderTree from './FolderTree';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Footer from './Footer';
import full_logo_black from '../assets/full_logo_black.png';

// Costante soglia minima di rilevanza (30%)
const MIN_RELEVANCE_THRESHOLD = 0.30;

// Componente Confirm Dialog
function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmText = "Conferma", cancelText = "Annulla", type = "danger" }) {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      icon: <AlertTriangle className="w-12 h-12 text-red-500" />,
      confirmBg: 'bg-red-600 hover:bg-red-700',
      iconBg: 'bg-red-100'
    },
    warning: {
      icon: <AlertCircle className="w-12 h-12 text-yellow-500" />,
      confirmBg: 'bg-yellow-600 hover:bg-yellow-700',
      iconBg: 'bg-yellow-100'
    },
    info: {
      icon: <AlertCircle className="w-12 h-12 text-blue-500" />,
      confirmBg: 'bg-blue-600 hover:bg-blue-700',
      iconBg: 'bg-blue-100'
    }
  };

  const config = typeConfig[type];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className={`${config.iconBg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
            {config.icon}
          </div>
          
          <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center whitespace-pre-wrap mb-6">
            {message}
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition font-medium ${config.confirmBg}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

// Componente Snackbar
function Snackbar({ message, type = 'info', onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const typeConfig = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      closeColor: 'text-green-500 hover:text-green-700',
      progressBg: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      closeColor: 'text-red-500 hover:text-red-700',
      progressBg: 'bg-red-500'
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
      closeColor: 'text-yellow-500 hover:text-yellow-700',
      progressBg: 'bg-yellow-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: <AlertCircle className="w-5 h-5 text-blue-500" />,
      closeColor: 'text-blue-500 hover:text-blue-700',
      progressBg: 'bg-blue-500'
    }
  };

  const config = typeConfig[type];

  return (
    <div
      className={`transform transition-all duration-300 ${
        isExiting 
          ? 'translate-x-full opacity-0' 
          : 'translate-x-0 opacity-100'
      }`}
    >
      <div className={`${config.bg} ${config.border} border rounded-lg shadow-lg p-4 flex items-start mb-2`}>
        <div className="flex-shrink-0 mr-3 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1">
          <p className={`text-sm ${config.text} whitespace-pre-wrap`}>{message}</p>
        </div>
        <button
          onClick={handleClose}
          className={`flex-shrink-0 ml-3 ${config.closeColor} transition-colors`}
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="h-1 bg-gray-200 rounded-b-lg overflow-hidden -mt-2 mb-2">
        <div
          className={`h-full ${config.progressBg}`}
          style={{
            animation: `progress ${duration}ms linear`,
            transformOrigin: 'left'
          }}
        />
      </div>

      <style>{`
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

// formatta testo
function formatTextWithBold(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={index} className="font-bold">{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

// ====================  MAIN COMPONENT ====================
export default function Dashboard({ user, onLogout, onUpdateUser }) {
  // Stati base
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  //const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadFiles, setUploadFiles] = useState([]);

  // Stati per flusso anonimizzazione
  const [pendingUpload, setPendingUpload] = useState(null); // { files, folderPath }
  const [anonymizationDialogOpen, setAnonymizationDialogOpen] = useState(false);
  const [anonymizationQueue, setAnonymizationQueue] = useState([]);
  // Ogni entry: { id, jobId, filename, folderPath, file, status, anonymizedUrl,
  //               anonymizedText, editedText, entities, error, pollTicks, consecutiveErrors }
  const [editingJobId, setEditingJobId] = useState(null);
  const [editorText, setEditorText] = useState('');
  const [editorSaving, setEditorSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [querying, setQuerying] = useState(false);
  
  // Stati per notifiche snackbar
  const [notifications, setNotifications] = useState([]);
  
  // Stati per dialog di conferma
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });
  
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('query');
  const [isDragging, setIsDragging] = useState(false);
  
  // Stati per ordinamento e filtri
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  
  // Stati per modal visualizzazione documento
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [loadingDocument, setLoadingDocument] = useState(false);
  // Stato per il sotto-modal di anteprima del file originale (documenti anonimizzati)
  // Forma: { objectUrl, mime, filename } | null
  const [originalPreview, setOriginalPreview] = useState(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  // Stato per l'anteprima inline del file originale nel modale principale,
  // usata SOLO per i documenti NON anonimizzati con tipo visualizzabile inline (PDF/immagini).
  // Forma: { objectUrl, mime, filename } | null
  const [inlineOriginal, setInlineOriginal] = useState(null);

  // Stati per storico query
  const [queryHistory, setQueryHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [expandedHistoryDocs, setExpandedHistoryDocs] = useState({});


  // Stato per visualizzazione risultati query
  const [showAllResults, setShowAllResults] = useState(false);

  // ========== NUOVI STATI PER CARTELLE ==========
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('ALL');
  const [selectedFoldersForQuery, setSelectedFoldersForQuery] = useState([]);
  // Array vuoto = tutte le cartelle; non vuoto = filtro sulle cartelle selezionate
  // ==============================================

  const [usageRefreshTrigger, setUsageRefreshTrigger] = useState(0);

  // Funzioni per gestire le notifiche
  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const showSuccess = (message) => addNotification(message, 'success', 5000);
  const showError = (message) => addNotification(message, 'error', 7000);
  const showWarning = (message) => addNotification(message, 'warning', 5000);
  const showInfo = (message) => addNotification(message, 'info', 4000);

  // Funzioni per gestire il dialog di conferma
  const showConfirmDialog = (title, message, onConfirm, type = 'danger') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
      type: 'danger'
    });
  };

  // Carica documenti, cartelle e storico all'avvio
  useEffect(() => {
    loadDocuments();
    loadFolders(); // NUOVO
    loadStats();
    if (activeTab === 'analytics' || activeTab === 'history') {
      loadQueryHistory();
    }
  }, [activeTab]);

  // Idratazione coda anonimizzazione al mount (sopravvive a refresh/chiusura tab)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.getPendingAnonymizations();
        if (cancelled) return;
        const records = resp.pending || [];
        if (records.length === 0) return;
        const entries = records.map(r => queueEntryFromServer(r));
        setAnonymizationQueue(prev => {
          // Evita doppioni in dev StrictMode
          const existing = new Set(prev.map(q => q.id));
          return [...prev, ...entries.filter(e => !existing.has(e.id))];
        });
      } catch (err) {
        console.error('Errore idratazione coda anonimizzazione:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling job di anonimizzazione (ogni 2s finché ci sono job in stato 'polling')
  // Usa l'endpoint persistente /pending/<id>/poll: il backend cacha il testo
  // anonimizzato e aggiorna il record, così il flusso sopravvive al refresh.
  useEffect(() => {
    const hasPollable = anonymizationQueue.some(j => j.status === 'polling' && j.pendingId);
    if (!hasPollable) return;

    const MAX_POLL_TICKS = 900; // ~30 min a 2s/tick
    const MAX_CONSECUTIVE_ERRORS = 5;

    const intervalId = setInterval(async () => {
      const pollableNow = anonymizationQueue.filter(j => j.status === 'polling' && j.pendingId);

      await Promise.all(pollableNow.map(async (entry) => {
        try {
          const data = await api.pollPendingAnonymization(entry.pendingId);
          const record = data.pending || {};
          const serverStatus = (record.status || '').toLowerCase();

          if (serverStatus === 'ready') {
            setAnonymizationQueue(prev => prev.map(q =>
              q.id === entry.id
                ? {
                    ...q,
                    status: 'ready',
                    anonymizedText: record.anonymized_text ?? q.anonymizedText,
                    editedText: q.editedText ?? record.anonymized_text ?? null,
                    entities: record.entities || q.entities || [],
                    consecutiveErrors: 0,
                    error: null,
                  }
                : q
            ));
          } else if (serverStatus === 'error') {
            setAnonymizationQueue(prev => prev.map(q =>
              q.id === entry.id
                ? { ...q, status: 'error', error: record.error_message || 'Anonimizzazione fallita' }
                : q
            ));
          } else {
            // submitted / processing — incrementa tick
            setAnonymizationQueue(prev => prev.map(q => {
              if (q.id !== entry.id) return q;
              const nextTicks = (q.pollTicks || 0) + 1;
              if (nextTicks > MAX_POLL_TICKS) {
                return { ...q, status: 'error', error: 'Timeout: anonimizzazione troppo lunga' };
              }
              return { ...q, pollTicks: nextTicks, consecutiveErrors: 0 };
            }));
          }
        } catch (err) {
          // 404 → record cancellato da un'altra tab: rimuovi silenziosamente
          if (err.status === 404) {
            setAnonymizationQueue(prev => prev.filter(q => q.id !== entry.id));
            return;
          }
          setAnonymizationQueue(prev => prev.map(q => {
            if (q.id !== entry.id) return q;
            const nextErrs = (q.consecutiveErrors || 0) + 1;
            if (nextErrs >= MAX_CONSECUTIVE_ERRORS) {
              return { ...q, status: 'error', error: `Errore di rete persistente: ${err.message}` };
            }
            return { ...q, consecutiveErrors: nextErrs };
          }));
        }
      }));
    }, 2000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymizationQueue.map(j => `${j.id}:${j.status}:${j.pendingId || ''}`).join('|')]);

  const loadDocuments = async (folderFilter = null) => {
    try {
      const response = await api.getDocuments(folderFilter);
      setDocuments(response.documents || []);
    } catch (err) {
      showError('Errore nel caricamento dei documenti');
    }
  };

  // NUOVA FUNZIONE: Carica cartelle
  const loadFolders = async () => {
    try {
      const response = await api.getFolders();
      setFolders(response.folders || []);
    } catch (err) {
      console.error('Errore caricamento cartelle:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.getStats();
      setStats(response);
    } catch (err) {
      console.error('Errore caricamento statistiche:', err);
    }
  };

  const loadQueryHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.getQueryHistory(100);
      setQueryHistory(response.queries || []);
    } catch (err) {
      showError('Errore nel caricamento dello storico: ' + err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ========== NUOVE FUNZIONI PER GESTIONE CARTELLE ==========
  
  const handleSelectFolder = (folderPath) => {
    setSelectedFolder(folderPath);
  };

  const handleCreateFolder = async (folderPath) => {
    try {
      await api.createFolder(folderPath);
      await loadFolders();
      showSuccess('Cartella creata con successo!');
    } catch (err) {
      showError('Errore creazione cartella: ' + err.message);
    }
  };

  const handleRenameFolder = async (oldPath, newPath) => {
    try {
      await api.renameFolder(oldPath, newPath);
      await loadDocuments();
      await loadFolders();
      showSuccess('Cartella rinominata con successo!');
    } catch (err) {
      showError('Errore rinomina cartella: ' + err.message);
    }
  };

  const handleDeleteFolder = async (folderPath) => {
    try {
      await api.deleteFolder(folderPath);
      await loadDocuments();
      await loadFolders();
      // Se la cartella eliminata era quella selezionata, torna a "Tutti"
      if (selectedFolder === folderPath || selectedFolder.startsWith(folderPath + '/')) {
        setSelectedFolder('ALL');
      }
      showSuccess('Cartella eliminata con successo!');
    } catch (err) {
      showError('Errore eliminazione cartella: ' + err.message);
    }
  };

  const handleMoveDocument = async (documentId, targetFolder) => {
    try {
      await api.moveDocument(documentId, targetFolder);
      await loadDocuments();
      showSuccess('Documento spostato con successo!');
    } catch (err) {
      showError('Errore spostamento documento: ' + err.message);
    }
  };

  // ==========================================================

  const handleDeleteHistory = async (queryId) => {
    showConfirmDialog(
      'Elimina Query',
      'Sei sicuro di voler eliminare questa query dallo storico?\nQuesta azione non può essere annullata.',
      async () => {
        try {
          await api.deleteQueryHistory(queryId);
          showSuccess('Query eliminata dallo storico');
          setQueryHistory(prev => prev.filter(q => q.id !== queryId));
          if (expandedQuery === queryId) {
            setExpandedQuery(null);
          }
        } catch (err) {
          showError('Errore nell\'eliminazione: ' + err.message);
        }
      },
      'danger'
    );
  };

  const handleClearAllHistory = async () => {
    showConfirmDialog(
      'Elimina Tutto lo Storico',
      `Sei sicuro di voler eliminare TUTTE le ${queryHistory.length} query dallo storico?\n\nQuesta azione è irreversibile e cancellerà permanentemente tutte le query e le relative risposte.`,
      async () => {
        try {
          const deletePromises = queryHistory.map(q => api.deleteQueryHistory(q.id));
          await Promise.all(deletePromises);
          showSuccess('Storico completamente eliminato');
          setQueryHistory([]);
          setExpandedQuery(null);
        } catch (err) {
          showError('Errore nell\'eliminazione dello storico: ' + err.message);
        }
      },
      'danger'
    );
  };

  // Tipi di file per cui mostriamo l'anteprima inline del file originale
  // direttamente nel modale principale (al posto del testo estratto).
  const INLINE_PREVIEW_TYPES = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

  // Funzione per visualizzare un documento
  const handleViewDocument = async (documentId, documentName) => {
    const doc = documents.find(d => d.id === documentId);
    setViewingDocument({
      id: documentId,
      name: documentName || doc?.name || `Documento ${documentId}`,
      // Pre-popola has_original dalla lista (se disponibile) per evitare flicker del pulsante
      has_original: !!doc?.has_original,
    });
    setLoadingDocument(true);
    setDocumentContent('');
    // Reset di eventuale anteprima inline pendente da una precedente apertura
    setInlineOriginal(prev => {
      if (prev?.objectUrl) {
        try { URL.revokeObjectURL(prev.objectUrl); } catch (_) { /* ignore */ }
      }
      return null;
    });

    try {
      const response = await api.getDocument(documentId);
      setDocumentContent(response.document.content || 'Nessun contenuto disponibile');
      // Aggiorna il flag has_original con il valore canonico dal backend
      setViewingDocument(prev => prev && prev.id === documentId ? {
        ...prev,
        has_original: !!response.document.has_original,
        metadata: response.document.metadata || null,
      } : prev);

      // Decide se sostituire il testo estratto con un'anteprima inline del file originale:
      // SOLO per documenti NON anonimizzati, che hanno l'originale e tipo previewable.
      const isAnonymized = !!response.document.metadata?.anonymized;
      const hasOriginal = !!response.document.has_original;
      const fileType = (response.document.file_type || '').toLowerCase();
      const shouldRenderOriginal = !isAnonymized && hasOriginal && INLINE_PREVIEW_TYPES.includes(fileType);

      if (shouldRenderOriginal) {
        try {
          const { blob, mime, filename } = await api.getDocumentOriginal(documentId);
          // Race-condition guard: l'utente potrebbe aver aperto un altro documento
          // mentre il blob era in download.
          setViewingDocument(prev => {
            if (!prev || prev.id !== documentId) {
              return prev;
            }
            return prev;
          });
          const objectUrl = URL.createObjectURL(blob);
          setInlineOriginal({ objectUrl, mime, filename: filename || response.document.name });
        } catch (previewErr) {
          // Fallback silenzioso al testo estratto, log per diagnostica
          console.warn('Anteprima originale non disponibile, fallback al testo:', previewErr);
        }
      }
    } catch (err) {
      setDocumentContent('Errore nel caricamento del contenuto: ' + err.message);
    } finally {
      setLoadingDocument(false);
    }
  };

  // Apre il sotto-modal con l'anteprima del file originale per un documento
  // arbitrario (passato come parametro). Riusabile anche dalle liste dei
  // risultati query e dello storico, dove `viewingDocument` non è settato.
  const openOriginalForDocument = async (documentId, fallbackName) => {
    if (!documentId) return;
    setLoadingOriginal(true);
    try {
      const { blob, mime, filename } = await api.getDocumentOriginal(documentId);
      const objectUrl = URL.createObjectURL(blob);
      setOriginalPreview({
        objectUrl,
        mime,
        filename: filename || fallbackName || `documento_${documentId}`,
      });
    } catch (err) {
      showError('Impossibile caricare il file originale: ' + err.message);
    } finally {
      setLoadingOriginal(false);
    }
  };

  // Wrapper retro-compat: usato dal pulsante "Mostra originale" nel modale documento.
  const handleShowOriginal = () => {
    if (!viewingDocument?.id) return;
    openOriginalForDocument(viewingDocument.id, viewingDocument.name);
  };

  // Chiude il sotto-modal del file originale e libera l'object URL
  const handleCloseOriginalPreview = () => {
    if (originalPreview?.objectUrl) {
      try { URL.revokeObjectURL(originalPreview.objectUrl); } catch (_) { /* ignore */ }
    }
    setOriginalPreview(null);
  };

  // Chiudi modal
  const handleCloseModal = () => {
    handleCloseOriginalPreview();
    if (inlineOriginal?.objectUrl) {
      try { URL.revokeObjectURL(inlineOriginal.objectUrl); } catch (_) { /* ignore */ }
    }
    setInlineOriginal(null);
    setViewingDocument(null);
    setDocumentContent('');
  };

  // ========== GESTIONE FILE UPLOAD COMPLETA ==========
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    // Reset input file subito, così l'utente può riselezionare lo stesso file
    if (e.target) {
      e.target.value = '';
    }

    if (files.length === 0) return;
    askAnonymizationChoice(files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Solo se usciamo dal componente principale, non dai figli
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading) {
      return; // Non accettare nuovi file durante upload
    }

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (droppedFiles.length === 0) {
      return;
    }

    askAnonymizationChoice(droppedFiles);
  };

  // Mostra il popup di scelta anonimizzazione prima di avviare il caricamento
  const askAnonymizationChoice = (files) => {
    const folderPath = selectedFolder === 'ALL' ? '/' : selectedFolder;
    setPendingUpload({ files, folderPath });
    setAnonymizationDialogOpen(true);
  };

  const onAnonymizationChoice = (anonymize) => {
    const batch = pendingUpload;
    setAnonymizationDialogOpen(false);
    setPendingUpload(null);
    if (!batch) return;
    if (anonymize) {
      startAnonymizationFlow(batch.files, batch.folderPath);
    } else {
      processFiles(batch.files, batch.folderPath);
    }
  };

  // Mappa lo stato server-side al modello del queue frontend
  const queueEntryFromServer = (record) => {
    const serverStatus = (record.status || '').toLowerCase();
    // submitted/processing → polling lato UI; ready/error invariati
    const uiStatus = (serverStatus === 'submitted' || serverStatus === 'processing')
      ? 'polling'
      : serverStatus;
    return {
      id: record.id, // pending_id server-side (number)
      pendingId: record.id,
      jobId: record.job_id,
      filename: record.filename,
      folderPath: record.folder_path,
      file: null, // file originale non più disponibile dopo invio (e dopo refresh)
      status: uiStatus,
      anonymizedText: record.anonymized_text ?? null,
      editedText: record.anonymized_text ?? null,
      entities: record.entities || [],
      error: record.error_message || null,
      pollTicks: 0,
      consecutiveErrors: 0,
    };
  };

  const startAnonymizationFlow = async (files, folderPath) => {
    const allowedExtensions = ['txt', 'md', 'pdf', 'docx'];
    const maxSize = 16 * 1024 * 1024;

    const entriesToAdd = [];
    const skipped = [];

    files.forEach(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        skipped.push(`${file.name}: tipo non supportato`);
        return;
      }
      if (file.size > maxSize) {
        skipped.push(`${file.name}: troppo grande (max 16MB)`);
        return;
      }
      entriesToAdd.push({
        // Id temporaneo lato client; sostituito con pending_id server dopo /start
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        tempId: true,
        pendingId: null,
        jobId: null,
        filename: file.name,
        folderPath,
        file,
        status: 'submitting',
        anonymizedText: null,
        editedText: null,
        entities: [],
        error: null,
        pollTicks: 0,
        consecutiveErrors: 0,
      });
    });

    if (skipped.length > 0) {
      showError(`File ignorati:\n${skipped.join('\n')}`);
    }
    if (entriesToAdd.length === 0) return;

    setAnonymizationQueue(prev => [...prev, ...entriesToAdd]);

    await Promise.all(entriesToAdd.map(async (entry) => {
      try {
        const resp = await api.startAnonymization(entry.file, folderPath);
        const pendingId = resp.pending_id;
        // Sostituisce id temporaneo con pending_id server-side
        setAnonymizationQueue(prev => prev.map(q =>
          q.id === entry.id
            ? {
                ...q,
                id: pendingId,
                pendingId,
                tempId: false,
                jobId: resp.job_id,
                status: 'polling',
              }
            : q
        ));
      } catch (err) {
        setAnonymizationQueue(prev => prev.map(q =>
          q.id === entry.id ? { ...q, status: 'error', error: err.message } : q
        ));
      }
    }));
  };

  const retryAnonymizationEntry = async (entryId) => {
    const entry = anonymizationQueue.find(q => q.id === entryId);
    if (!entry || !entry.file) {
      // Senza il file originale (es. dopo refresh) non possiamo ritentare lo start;
      // l'utente deve ricaricare il file dal disco.
      showError('Per ritentare l\'anonimizzazione ricarica il file dal disco');
      return;
    }
    // Se c'era un pending_id, cancella il vecchio record server-side
    if (entry.pendingId) {
      try { await api.deletePendingAnonymization(entry.pendingId); } catch (_) { /* ignore */ }
    }
    // Rimuovi la entry corrente e ricomincia il flusso per quel file
    setAnonymizationQueue(prev => prev.filter(q => q.id !== entryId));
    startAnonymizationFlow([entry.file], entry.folderPath);
  };

  const uploadWithoutAnonymization = async (entryId) => {
    const entry = anonymizationQueue.find(q => q.id === entryId);
    if (!entry || !entry.file) {
      showError('Per caricare senza anonimizzazione ricarica il file dal disco');
      return;
    }
    setAnonymizationQueue(prev => prev.map(q =>
      q.id === entryId ? { ...q, status: 'finalizing', error: null } : q
    ));
    try {
      await api.uploadFile(entry.file, entry.folderPath);
      if (entry.pendingId) {
        try { await api.deletePendingAnonymization(entry.pendingId); } catch (_) { /* ignore */ }
      }
      setAnonymizationQueue(prev => prev.map(q =>
        q.id === entryId ? { ...q, status: 'done' } : q
      ));
      showSuccess(`${entry.filename} caricato (senza anonimizzazione)`);
      loadDocuments();
      loadFolders();
      loadStats();
      setTimeout(() => {
        setAnonymizationQueue(prev => prev.filter(q => q.id !== entryId));
      }, 3000);
    } catch (err) {
      setAnonymizationQueue(prev => prev.map(q =>
        q.id === entryId ? { ...q, status: 'error', error: err.message } : q
      ));
    }
  };

  const dismissAnonymizationEntry = async (entryId) => {
    const entry = anonymizationQueue.find(q => q.id === entryId);
    if (!entry) return;
    if (entry.pendingId) {
      try { await api.deletePendingAnonymization(entry.pendingId); } catch (_) { /* ignore */ }
    }
    setAnonymizationQueue(prev => prev.filter(q => q.id !== entryId));
    if (editingJobId === entryId) closeEditor();
  };

  const openEditor = (entryId) => {
    const entry = anonymizationQueue.find(q => q.id === entryId);
    if (!entry || entry.status !== 'ready') return;
    setEditingJobId(entryId);
    setEditorText(entry.editedText ?? entry.anonymizedText ?? '');
  };

  const closeEditor = () => {
    setEditingJobId(null);
    setEditorText('');
  };

  const saveAnonymizedDocument = async () => {
    const entry = anonymizationQueue.find(q => q.id === editingJobId);
    if (!entry || !entry.pendingId) return;
    setEditorSaving(true);
    setAnonymizationQueue(prev => prev.map(q =>
      q.id === entry.id ? { ...q, editedText: editorText, status: 'finalizing', error: null } : q
    ));
    try {
      await api.finalizePendingAnonymization(entry.pendingId, editorText);
      setAnonymizationQueue(prev => prev.map(q =>
        q.id === entry.id ? { ...q, status: 'done' } : q
      ));
      showSuccess(`${entry.filename} anonimizzato e salvato`);
      loadDocuments();
      loadFolders();
      loadStats();
      closeEditor();
      setTimeout(() => {
        setAnonymizationQueue(prev => prev.filter(q => q.id !== entry.id));
      }, 3000);
    } catch (err) {
      setAnonymizationQueue(prev => prev.map(q =>
        q.id === entry.id ? { ...q, status: 'ready', error: err.message } : q
      ));
    } finally {
      setEditorSaving(false);
    }
  };

  const processFiles = async (files, overrideFolderPath = null) => {
    const destinationFolder = overrideFolderPath !== null
      ? overrideFolderPath
      : (selectedFolder === 'ALL' ? '/' : selectedFolder);

    if (files.length === 0) {
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    setUploadFiles(files.map(f => f.name));

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        setUploadProgress({ current: i + 1, total: files.length });
        
        try {
          // Verifica dimensione file (max 16MB)
          const maxSize = 16 * 1024 * 1024;
          if (file.size > maxSize) {
            throw new Error(`File troppo grande (max 16MB)`);
          }
          
          // Verifica estensione
          const allowedExtensions = ['txt', 'md', 'pdf', 'docx'];
          const extension = file.name.split('.').pop().toLowerCase();
          if (!allowedExtensions.includes(extension)) {
            throw new Error(`Tipo di file non supportato (.${extension})`);
          }
          
          // MODIFICATO: Passa uploadFolder come secondo parametro
          const response = await api.uploadFile(file, destinationFolder);
          successCount++;
          
          console.log(`✅ File ${file.name} caricato:`, {
            document_id: response.document?.id,
            folder_path: destinationFolder,
            extracted_chars: response.extracted_chars,
            metadata: response.metadata
          });
          
        } catch (err) {
          errorCount++;
          errors.push(`${file.name}: ${err.message}`);
          console.error(`❌ Errore caricamento ${file.name}:`, err);
        }
      }
      
      // Mostra risultato
      if (successCount > 0) {
        showSuccess(
          `✅ ${successCount} documento/i caricato/i con successo in ${destinationFolder}!` +
          (errorCount > 0 ? ` (${errorCount} errori)` : '')
        );
        loadDocuments();
        loadFolders(); // NUOVO: Ricarica cartelle
        loadStats();
      }
      
      if (errorCount > 0 && successCount === 0) {
        showError(`Errori durante il caricamento:\n${errors.join('\n')}`);
      }
      
    } catch (err) {
      showError('Errore generale durante il caricamento: ' + err.message);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      setUploadFiles([]);
    }
  };

  // =================================================

  const handleProcessDocuments = async () => {
    if (selectedDocs.length === 0) {
      showError('Seleziona almeno un documento da processare');
      return;
    }

    // Filtra automaticamente i documenti già processati dalla selezione
    const selectedDocuments = documents.filter(d => selectedDocs.includes(d.id));
    const unprocessedDocs = selectedDocuments.filter(d => !d.chunk_count || d.chunk_count === 0);
    const alreadyProcessed = selectedDocuments.filter(d => d.chunk_count > 0);
    
    if (unprocessedDocs.length === 0) {
      showError('Nessun documento da processare. Tutti i documenti selezionati sono già stati processati.');
      return;
    }
    
    // Mostra un warning se ci sono documenti processati nella selezione
    if (alreadyProcessed.length > 0) {
      showInfo(`${alreadyProcessed.length} documento/i già processato/i verranno saltati. Processing di ${unprocessedDocs.length} documento/i...`);
    }

    setProcessing(true);

    try {
      // Processa solo i documenti non processati
      await api.batchProcess(unprocessedDocs.map(d => d.id), {
        max_words: 250,
        overlap: 20,
        embedding_model: 'text-embedding-3-small'
      });

      showSuccess(`Processing completato! ${unprocessedDocs.length} documento/i processato/i.`);
      setSelectedDocs([]);
      loadDocuments();
      loadStats();
      setUsageRefreshTrigger(t => t + 1);
    } catch (err) {
      showError('Errore durante il processing: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteDocuments = async () => {
    if (selectedDocs.length === 0) {
      showError('Seleziona almeno un documento da eliminare');
      return;
    }

    showConfirmDialog(
      'Elimina Documenti',
      `Sei sicuro di voler eliminare ${selectedDocs.length} documento/i?\n\nQuesta azione eliminerà anche tutti i chunks e gli embeddings associati e non può essere annullata.`,
      async () => {
        try {
          const deletePromises = selectedDocs.map(id => api.deleteDocument(id));
          await Promise.all(deletePromises);
          
          showSuccess(`${selectedDocs.length} documento/i eliminato/i con successo`);
          setSelectedDocs([]);
          loadDocuments();
          loadFolders(); // NUOVO: Ricarica cartelle
          loadStats();
        } catch (err) {
          showError('Errore durante l\'eliminazione: ' + err.message);
        }
      },
      'danger'
    );
  };

  // ========== GESTIONE QUERY RAG ==========
  
  const handleToggleFolderForQuery = (folderPath) => {
    if (folderPath === 'ALL') {
      setSelectedFoldersForQuery([]);
      return;
    }
    setSelectedFoldersForQuery(prev =>
      prev.includes(folderPath)
        ? prev.filter(p => p !== folderPath)
        : [...prev, folderPath]
    );
  };

  const handlePerformQuery = async () => {
  if (!query.trim()) {
    showError('Inserisci una domanda');
    return;
  }

  setQuerying(true);
  setQueryResult(null);
  setResults([]); // 👈 Resetta i risultati

  try {
    const options = { 
      top_k: 99999
    };
    
    if (selectedFoldersForQuery.length > 0) {
      options.folder_paths = selectedFoldersForQuery;
    }

    const response = await api.performQuery(query, options);
    
    // 👇 TRASFORMA LA RISPOSTA NEL FORMATO ATTESO
    const transformedResult = {
      content: response.answer,  // answer → content
      ranked_documents: response.sources ? transformSources(response.sources) : [],
      bestDocument: response.sources && response.sources.length > 0 
        ? {
            id: response.sources[0].document_id,
            name: response.sources[0].document_name,
            score: response.sources[0].similarity || 0
          }
        : null
    };
    
    setQueryResult(response);
    setResults([transformedResult]); // 👈 Usa il formato trasformato
    await loadQueryHistory();
    showSuccess('Query eseguita con successo!');
    setUsageRefreshTrigger(t => t + 1);
  } catch (err) {
    showError('Errore durante la query: ' + err.message);
  } finally {
    setQuerying(false);
  }
};

// 👇 AGGIUNGI QUESTA FUNZIONE DI TRASFORMAZIONE (fuori da handlePerformQuery)
const transformSources = (sources) => {
  // Raggruppa i sources per documento
  const docMap = {};
  
  sources.forEach(source => {
    const docId = source.document_id;
    
    if (!docMap[docId]) {
      docMap[docId] = {
        document_id: docId,
        document_name: source.document_name,
        folder_path: source.folder_path,
        has_original: !!source.has_original,
        anonymized: !!source.anonymized,
        original_filename: source.original_filename || null,
        chunk_count: 0,
        avg_similarity: 0,
        max_similarity: 0,
        score: 0,
        top_chunks: []
      };
    }
    
    docMap[docId].chunk_count++;
    docMap[docId].top_chunks.push({
      chunk_index: source.chunk_index,
      content: source.chunk_content,
      similarity: source.similarity || 0
    });
    
    const similarity = source.similarity || 0;
    docMap[docId].max_similarity = Math.max(docMap[docId].max_similarity, similarity);
  });
  
  // Calcola medie e punteggi
  const rankedDocs = Object.values(docMap).map(doc => {
    const similarities = doc.top_chunks.map(c => c.similarity);
    doc.avg_similarity = similarities.reduce((a, b) => a + b, 0) / similarities.length;
    doc.score = doc.max_similarity * 0.7 + doc.avg_similarity * 0.3; // 70% max, 30% media
    
    // Ordina i chunks per similarità
    doc.top_chunks.sort((a, b) => b.similarity - a.similarity);
    doc.top_chunks = doc.top_chunks.slice(0, 3); // Prendi solo i top 3
    
    return doc;
  });
  
  // Ordina per score e aggiungi rank
  rankedDocs.sort((a, b) => b.score - a.score);
  rankedDocs.forEach((doc, idx) => {
    doc.rank = idx + 1;
  });
  
  return rankedDocs;
};

  // ========================================

  const handleUpdateProfile = async (updates) => {
    try {
      // Chiama API per aggiornare profilo
      // await api.updateCurrentUser(updates);
      onUpdateUser({ ...user, ...updates });
      showSuccess('Profilo aggiornato con successo!');
    } catch (err) {
      showError('Errore aggiornamento profilo: ' + err.message);
    }
  };

  // Toggle selezione documenti
  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDocs.length === filteredAndSortedDocuments.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(filteredAndSortedDocuments.map(d => d.id));
    }
  };

  // Filtra e ordina documenti in base a selectedFolder
  const getFilteredDocuments = () => {
    let filtered = documents;

    // NUOVO: Filtro per cartella
    if (selectedFolder !== 'ALL') {
      filtered = documents.filter(doc => {
        const docFolder = doc.folder_path || '/';
        // Mostra documenti nella cartella e nelle sottocartelle
        return docFolder === selectedFolder || docFolder.startsWith(selectedFolder + '/');
      });
    }

    // Filtro per ricerca testuale (nome anonimizzato + nome originale)
    if (documentSearchTerm.trim()) {
      const searchLower = documentSearchTerm.trim().toLowerCase();
      filtered = filtered.filter(d => {
        const name = (d.name || '').toLowerCase();
        const original = (d.metadata?.original_filename || '').toLowerCase();
        return name.includes(searchLower) || original.includes(searchLower);
      });
    }

    // Filtro per stato
    if (filterStatus === 'processed') {
      filtered = filtered.filter(d => d.chunk_count > 0);
    } else if (filterStatus === 'unprocessed') {
      filtered = filtered.filter(d => !d.chunk_count || d.chunk_count === 0);
    }

    // Ordinamento
    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'date':
          compareValue = new Date(a.created_at) - new Date(b.created_at);
          break;
        case 'size':
          compareValue = (a.file_size || 0) - (b.file_size || 0);
          break;
        case 'chunks':
          compareValue = (a.chunk_count || 0) - (b.chunk_count || 0);
          break;
        default:
          compareValue = 0;
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  };

  const filteredAndSortedDocuments = getFilteredDocuments();

  // Conta documenti selezionati non ancora processati
  const selectedUnprocessed = selectedDocs.filter(id => {
    const doc = documents.find(d => d.id === id);
    return doc && (!doc.chunk_count || doc.chunk_count === 0);
  }).length;

  // Filtra storico query
  const filteredHistory = queryHistory.filter(q => {
    if (!historySearchTerm) return true;
    const searchLower = historySearchTerm.toLowerCase();
    return (
      q.query_text.toLowerCase().includes(searchLower) ||
      (q.response && q.response.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Notifications Container */}
      <div className="fixed top-4 right-4 z-50 max-w-md">
        {notifications.map(notification => (
          <Snackbar
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* Dialog scelta anonimizzazione */}
      {anonymizationDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                Anonimizzare i documenti?
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Vuoi anonimizzare i documenti caricati prima di salvarli? Potrai rivedere
                e modificare il testo anonimizzato prima della conferma finale.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => onAnonymizationChoice(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  No, carica come sono
                </button>
                <button
                  onClick={() => onAnonymizationChoice(true)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Sì, anonimizza
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor testo anonimizzato */}
      {editingJobId && (() => {
        const entry = anonymizationQueue.find(q => q.id === editingJobId);
        if (!entry) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <FileText className="w-6 h-6 mr-2 text-blue-600" />
                    Revisiona testo anonimizzato: {entry.filename}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Entità rilevate: {entry.entities?.length || 0}. Modifica liberamente prima di salvare.
                  </p>
                </div>
                <button
                  onClick={closeEditor}
                  className="text-gray-400 hover:text-gray-600 transition"
                  disabled={editorSaving}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-6">
                <textarea
                  value={editorText}
                  onChange={(e) => setEditorText(e.target.value)}
                  disabled={editorSaving}
                  className="w-full h-full min-h-[400px] font-mono text-sm border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {entry.error && (
                <div className="px-6 pb-2 text-sm text-red-600">
                  {entry.error}
                </div>
              )}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={closeEditor}
                  disabled={editorSaving}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium disabled:opacity-50"
                >
                  Annulla
                </button>
                <button
                  onClick={saveAnonymizedDocument}
                  disabled={editorSaving || !editorText.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 flex items-center"
                >
                  {editorSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Salvataggio...
                    </>
                  ) : (
                    'Approva'
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Visualizzazione Documento */}
      {viewingDocument && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <FileText className="w-6 h-6 mr-2 text-blue-600" />
                {viewingDocument.name}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-6 flex-1">
              {loadingDocument ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">Caricamento contenuto...</span>
                </div>
              ) : inlineOriginal && inlineOriginal.mime && inlineOriginal.mime.startsWith('application/pdf') ? (
                <iframe
                  title="Anteprima documento"
                  src={inlineOriginal.objectUrl}
                  className="w-full h-[75vh] border border-gray-200 rounded bg-white"
                />
              ) : inlineOriginal && inlineOriginal.mime && inlineOriginal.mime.startsWith('image/') ? (
                <div className="w-full h-[75vh] flex items-center justify-center bg-white border border-gray-200 rounded">
                  <img
                    src={inlineOriginal.objectUrl}
                    alt={inlineOriginal.filename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="" style={{
                  width: '100%',
                }}>
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-lg"
                    style={{
                      backgroundColor: 'white',
                      color: 'black',
                      width: '100%',
                    }}>
                    {documentContent}
                  </pre>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              {viewingDocument.has_original && !inlineOriginal && (
                <button
                  onClick={handleShowOriginal}
                  disabled={loadingOriginal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition flex items-center"
                  title="Visualizza il file originale (non anonimizzato)"
                >
                  {loadingOriginal ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Mostra originale
                </button>
              )}
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
              >
                Chiudi
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Sotto-modal: anteprima del file originale (per documenti anonimizzati).
          Renderizzato come blocco TOP-LEVEL e indipendente da `viewingDocument`,
          così può essere aperto anche dalla lista risultati query / storico
          (dove `viewingDocument` è null). */}
      {originalPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4"
          onClick={handleCloseOriginalPreview}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                File originale: {originalPreview.filename}
              </h3>
              <button
                onClick={handleCloseOriginalPreview}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content: PDF/immagine inline, altrimenti fallback download */}
            <div className="flex-1 overflow-hidden p-4 bg-gray-50">
              {originalPreview.mime && originalPreview.mime.startsWith('application/pdf') ? (
                <iframe
                  title="Anteprima file originale"
                  src={originalPreview.objectUrl}
                  className="w-full h-[75vh] border border-gray-200 rounded bg-white"
                />
              ) : originalPreview.mime && originalPreview.mime.startsWith('image/') ? (
                <div className="w-full h-[75vh] flex items-center justify-center bg-white border border-gray-200 rounded">
                  <img
                    src={originalPreview.objectUrl}
                    alt={originalPreview.filename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-full h-[40vh] flex flex-col items-center justify-center text-gray-600 gap-3">
                  <AlertCircle className="w-10 h-10 text-yellow-500" />
                  <p>Anteprima non disponibile per questo tipo di file ({originalPreview.mime || 'tipo sconosciuto'}).</p>
                  <a
                    href={originalPreview.objectUrl}
                    download={originalPreview.filename}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Scarica file
                  </a>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <a
                href={originalPreview.objectUrl}
                download={originalPreview.filename}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica
              </a>
              <button
                onClick={handleCloseOriginalPreview}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <img 
                  src={full_logo_black} 
                  alt="SematorPRO" 
                  style={{ 
                    width: '100%',
                    maxWidth: '400px' 
                  }}
                />
                {/*
                <h1 className="text-2xl font-bold text-gray-900">SemantorPRO</h1>
                <p className="text-sm text-gray-500">Sistema di retrieval con AI</p>
                */}
              </div>
            </div>

            {/* Logo azienda utente loggato */}
            {user?.company?.logo && (
              <div className="flex items-center space-x-2 pl-4">
                <img 
                  src={user.company.logo} 
                  alt={user.company.ragione_sociale}
                  className="h-16 w-auto object-contain"
                  title={user.company.ragione_sociale}
                />
              </div>
            )}
            
            <div className="flex items-center space-x-4">
              {stats && (
                <div className="flex items-center space-x-4 text-sm text-gray-600 border-l pl-4">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    <span>{stats.total_documents || 0} docs</span>
                  </div>
                </div>
              )}
              
              <UserAccountDropdown 
                user={user}
                onLogout={onLogout}
                onUpdateProfile={handleUpdateProfile}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-8 sm:px-8 lg:px-8 py-8">
        {/* Widget utilizzo piano - solo per utenti non-admin con azienda */}
        {!user?.is_superuser && user?.company_id && (
          <UsageWidget refreshTrigger={usageRefreshTrigger} />
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Analytics
            </button>
            
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-5 h-5 inline mr-2" />
              Documenti
            </button>
            
            <button
              onClick={() => setActiveTab('query')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'query'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Search className="w-5 h-5 inline mr-2" />
              Query semantica
            </button>
            
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <History className="w-5 h-5 inline mr-2" />
              Storico
              {queryHistory.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {queryHistory.length}
                </span>
              )}
            </button>

            {/* Tab Gestione Aziende - SOLO per superuser */}
            {user?.is_superuser && (
              <button
                onClick={() => setActiveTab('companies')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                  activeTab === 'companies'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building className="w-5 h-5 inline mr-2" />
                Gestione Aziende
              </button>
            )}

            {/* Tab Gestione Utenti - SOLO per superuser */}
            {user?.is_superuser && (
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="w-5 h-5 inline mr-2" />
                Gestione Utenti
              </button>
            )}

            {/* Tab Abbonamenti - SOLO per superuser */}
            {user?.is_superuser && (
              <button
                onClick={() => setActiveTab('subscriptions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                  activeTab === 'subscriptions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <CreditCard className="w-5 h-5 inline mr-2" />
                Abbonamenti
              </button>
            )}

            {/* Tab Backfill Originali - SOLO per superuser */}
            {user?.is_superuser && (
              <button
                onClick={() => setActiveTab('backfill_originals')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                  activeTab === 'backfill_originals'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Upload className="w-5 h-5 inline mr-2" />
                Import originali
              </button>
            )}
          </nav>
        </div>

        {/* Tab content - Analytics */}
        {activeTab === 'analytics' && (
           <div className="space-y-6">
              <AnalyticsDashboard
                queryHistory={queryHistory}
                documents={documents}
                onViewDocument={handleViewDocument}
                loading={false}
              />
          </div>
        )}

        {/* ========== TAB DOCUMENTI CON FOLDERTREE ========== */}
        {activeTab === 'documents' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar con FolderTree */}
            <div className="col-span-3">
              <div className="sticky top-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                <FolderTree
                  folders={folders}
                  documents={documents}
                  selectedFolder={selectedFolder}
                  onSelectFolder={handleSelectFolder}
                  onCreateFolder={handleCreateFolder}
                  onRenameFolder={handleRenameFolder}
                  onDeleteFolder={handleDeleteFolder}
                  showActions={true}
                />
              </div>
            </div>

            {/* Area principale documenti */}
            <div className="col-span-9 space-y-6">
              {/* Upload Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Carica Documenti
                </h2>
                
                {/* Area di upload */}

                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Cartella di destinazione:</strong>{' '}
                    {selectedFolder === 'ALL' ? (
                      <span className="font-mono">Tutti i documenti</span>
                    ) : (
                      <span className="font-mono">{selectedFolder}</span>
                    )}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Seleziona una cartella dall'albero a sinistra per cambiare la destinazione
                  </p>
                </div>

                <label className="block">
                  <input
                    type="file"
                    multiple
                    accept=".txt,.md,.pdf,.docx"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                      isDragging 
                        ? 'border-blue-500 bg-blue-50 scale-105' 
                        : uploading
                          ? 'border-gray-300 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
                        <p className="text-sm text-gray-600 font-medium">
                          Caricamento in corso... ({uploadProgress.current}/{uploadProgress.total})
                        </p>
                        {uploadFiles.length > 0 && uploadProgress.current <= uploadFiles.length && (
                          <p className="text-xs text-gray-500 mt-2">
                            File corrente: {uploadFiles[uploadProgress.current - 1]}
                          </p>
                        )}
                      </>
                    ) : isDragging ? (
                      <>
                        <Upload className="w-8 h-8 mx-auto mb-2 text-blue-600 animate-bounce" />
                        <p className="text-sm text-blue-600 font-medium">
                          Rilascia i file qui
                        </p>
                        <p className="text-xs text-blue-500 mt-1">
                          Supportati: TXT, MD, PDF, DOCX
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          Clicca per caricare o trascina i file qui
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Supportati: TXT, MD, PDF, DOCX (max 16MB)
                        </p>
                      </>
                    )}
                  </div>
                </label>
              </div>

              {/* Coda anonimizzazione */}
              {anonymizationQueue.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold flex items-center mb-4">
                    <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                    Anonimizzazione in corso ({anonymizationQueue.length})
                  </h2>
                  <div className="space-y-2">
                    {anonymizationQueue.map(entry => {
                      const badge = (() => {
                        switch (entry.status) {
                          case 'submitting': return { text: 'Invio...', cls: 'bg-gray-100 text-gray-700' };
                          case 'polling': return { text: 'In elaborazione...', cls: 'bg-blue-100 text-blue-700' };
                          case 'ready': return { text: 'Pronto per la revisione', cls: 'bg-yellow-100 text-yellow-800' };
                          case 'finalizing': return { text: 'Salvataggio...', cls: 'bg-blue-100 text-blue-700' };
                          case 'done': return { text: 'Salvato', cls: 'bg-green-100 text-green-700' };
                          case 'error': return { text: 'Errore', cls: 'bg-red-100 text-red-700' };
                          default: return { text: entry.status, cls: 'bg-gray-100 text-gray-700' };
                        }
                      })();
                      return (
                        <div key={entry.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span className="font-medium text-sm truncate">{entry.filename}</span>
                              <span className="text-xs text-gray-400">→ {entry.folderPath}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
                              {(entry.status === 'submitting' || entry.status === 'polling' || entry.status === 'finalizing') && (
                                <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                              )}
                              {entry.error && (
                                <span className="text-xs text-red-600 truncate">{entry.error}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            {entry.status === 'ready' && (
                              <button
                                onClick={() => openEditor(entry.id)}
                                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                              >
                                Approva
                              </button>
                            )}
                            {entry.status === 'error' && (
                              <>
                                <button
                                  onClick={() => retryAnonymizationEntry(entry.id)}
                                  className="px-3 py-1.5 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition"
                                >
                                  Riprova
                                </button>
                                <button
                                  onClick={() => uploadWithoutAnonymization(entry.id)}
                                  className="px-3 py-1.5 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300 transition"
                                >
                                  Salva senza anonimizzazione
                                </button>
                              </>
                            )}
                            {entry.status !== 'done' && entry.status !== 'finalizing' && (
                              <button
                                onClick={() => dismissAnonymizationEntry(entry.id)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Rimuovi dalla coda"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Lista Documenti */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Documenti in {selectedFolder === 'ALL' ? 'Tutte le Cartelle' : selectedFolder} ({filteredAndSortedDocuments.length})
                  </h2>
                  
                  {selectedDocs.length > 0 && (
                    <div className="flex items-center gap-2">
                      {selectedUnprocessed > 0 && (
                        <button
                          onClick={handleProcessDocuments}
                          disabled={processing}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition flex items-center"
                        >
                          {processing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Brain className="w-4 h-4 mr-2" />
                              Processa ({selectedUnprocessed})
                            </>
                          )}
                        </button>
                      )}
                      
                      <button
                        onClick={handleDeleteDocuments}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Elimina ({selectedDocs.length})
                      </button>
                    </div>
                  )}
                </div>

                {/* Filtri e ordinamento */}
                <div className="flex items-center gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Cerca per nome..."
                    value={documentSearchTerm}
                    onChange={(e) => setDocumentSearchTerm(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />

                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tutti</option>
                    <option value="processed">Processati</option>
                    <option value="unprocessed">Non processati</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="date">Data</option>
                    <option value="name">Nome</option>
                    <option value="size">Dimensione</option>
                    <option value="chunks">Chunks</option>
                  </select>

                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                  </button>
                </div>

                {filteredAndSortedDocuments.length === 0 ? (
                  <div className="text-center py-12">
                    <Folder className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">
                      {selectedFolder === 'ALL' 
                        ? 'Nessun documento caricato' 
                        : `Nessun documento in ${selectedFolder}`}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                      <input
                        type="checkbox"
                        checked={selectedDocs.length === filteredAndSortedDocuments.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-600">Seleziona tutto</span>
                    </div>

                    {filteredAndSortedDocuments.map(doc => (
                      <div 
                        key={doc.id} 
                        className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.id)}
                          onChange={() => toggleDocSelection(doc.id)}
                          className="w-4 h-4"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{doc.name}</span>
                            {doc.chunk_count > 0 && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Processato
                              </span>
                            )}
                          </div>
                          {doc.metadata?.anonymized && doc.metadata?.original_filename && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Nome originale: {doc.metadata.original_filename}
                            </div>
                          )}
                          <div className="text-sm text-gray-500 mt-1">
                            {doc.folder_path || '/'} • {doc.chunk_count || 0} chunks • {new Date(doc.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Dropdown per spostare documento */}
                          <select
                            onChange={(e) => {
                              if (e.target.value && e.target.value !== doc.folder_path) {
                                handleMoveDocument(doc.id, e.target.value);
                              }
                              e.target.value = '';
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded"
                            defaultValue=""
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">Sposta in...</option>
                            <option value="/">Tutti i documenti</option>
                            {folders.map(folder => (
                              <option key={folder} value={folder}>
                                {folder}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => handleViewDocument(doc.id, doc.name)}
                            className="p-2 hover:bg-blue-100 rounded transition"
                            title="Visualizza"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ================================================== */}

        {/* ========== TAB QUERY RAG CON FOLDERTREE ========== */}
        {activeTab === 'query' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar con FolderTree per selezione cartella */}
            <div className="col-span-3">
              <div className="sticky top-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                <FolderTree
                  folders={folders}
                  documents={documents}
                  showActions={false}
                  multiSelect={true}
                  selectedFolders={selectedFoldersForQuery}
                  onToggleFolder={handleToggleFolderForQuery}
                />
              </div>
            </div>

            {/* Area principale query */}
            <div className="col-span-9 space-y-6">
              {/* Query Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Search className="w-5 h-5 mr-2" />
                  Query semantica
                </h2>
                
                {/* Info cartella selezionata */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Ricerca in:</strong>{' '}
                    {selectedFoldersForQuery.length === 0
                      ? 'Tutte le cartelle'
                      : selectedFoldersForQuery.length === 1
                        ? selectedFoldersForQuery[0]
                        : `${selectedFoldersForQuery.length} cartelle selezionate`}
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Fai una domanda sui documenti caricati..."
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handlePerformQuery()}
                  />
                  <button
                    onClick={handlePerformQuery}
                    disabled={querying || !query.trim()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center transition"
                  >
                    {querying ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Ricerca...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Cerca
                      </>
                    )}
                  </button>
                </div>
              </div>

            {querying && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <div className="flex items-center justify-center py-2">
                      {/*<Loader2 className="w-8 h-8 animate-spin text-blue-600" />*/}
                      <DotLottieReact
                      src="https://lottie.host/19afbdae-3b96-451d-9047-f53c2b275a9e/JLETFWAOYn.lottie"
                      loop
                      autoplay
                      style={{
                          maxWidth: "500px",
                          width: "100%",
                      }}
                      />
                  </div>
                  <div className="flex items-center justify-center py-2">
                    <span className="ml-3 text-gray-400 font-bold">Ricerca nei documenti in corso...</span>
                  </div>
                </div>
            )}

            {/* Results */}
            {results.length > 0 && (
              <div className="space-y-6">
                <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-l-4 border-green-500 shadow-md">
                  <h3 className="font-bold text-green-700 text-xl mb-4 flex items-center">
                    <Brain className="w-6 h-6 mr-2" />
                    Risposta AI (basata sui documenti)
                  </h3>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <pre className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {formatTextWithBold(results[0].content)}
                    </pre>
                  </div>
                  
                  {results[0].bestDocument && results[0].bestDocument.score >= MIN_RELEVANCE_THRESHOLD && (
                    <div className="mt-4 p-3 bg-white rounded-lg border-l-4 border-blue-500">
                      <p className="text-sm text-blue-700">
                        <strong>📄 Documento principale utilizzato:</strong>{' '}
                        <button
                          onClick={() => handleViewDocument(results[0].bestDocument.id, results[0].bestDocument.name)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center"
                        >
                          {results[0].bestDocument.name}
                          <Eye className="w-3 h-3 ml-1" />
                        </button>
                        <span className="ml-2 text-xs">
                          (Score: {(results[0].bestDocument.score * 100).toFixed(1)}%)
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                {results[0].ranked_documents && results[0].ranked_documents.length > 0 && (
                  results[0].bestDocument && results[0].bestDocument.score >= MIN_RELEVANCE_THRESHOLD ? (
                  <div className="space-y-4">
                    <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center text-lg">
                        <Database className="w-5 h-5 mr-2" />
                        Statistiche di Ricerca
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                          <div className="text-3xl font-bold text-blue-600">
                            {results[0].ranked_documents.length}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Documenti Analizzati</div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                          <div className="text-3xl font-bold text-green-600">
                            {results[0].ranked_documents.reduce((sum, doc) => sum + doc.chunk_count, 0)}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Sezioni Totali</div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                          <div className="text-3xl font-bold text-purple-600">
                            {(
                              (results[0].ranked_documents.reduce((sum, doc) => sum + doc.avg_similarity, 0) /
                                results[0].ranked_documents.length) *
                              100
                            ).toFixed(1)}
                            %
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Similarità Media</div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                          <div className="text-3xl font-bold text-orange-600">
                            {(Math.max(...results[0].ranked_documents.map((doc) => doc.score)) * 100).toFixed(1)}%
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Miglior Match</div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Database className="w-7 h-7 mr-2 text-yellow-500" />
                        Classifica Documenti per Rilevanza
                      </h3>
                      <span className="text-sm text-gray-500">
                        {results[0].ranked_documents.length} documenti analizzati
                      </span>
                    </div>

                    {/* Determina quanti risultati mostrare */}
                    {(() => {
                      const allDocs = results[0].ranked_documents;
                      
                      // FILTRO per similarità media > MIN_RELEVANCE_THRESHOLD
                      const filteredDocs = allDocs.filter(doc => doc.avg_similarity > MIN_RELEVANCE_THRESHOLD);
                      
                      const docsToShow = showAllResults ? filteredDocs : filteredDocs.slice(0, 5);
                      const remainingCount = filteredDocs.length - 5;
                      
                      return (
                        <>
                          {filteredDocs.length === 0 ? (
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                              <div className="flex items-center mb-2">
                                <AlertCircle className="w-6 h-6 text-yellow-600 mr-2" />
                                <h4 className="text-lg font-semibold text-yellow-800">
                                  Nessun documento supera la soglia di rilevanza
                                </h4>
                              </div>
                              <p className="text-yellow-700">
                                Nessun documento ha una similarità media superiore al 30%. 
                                I documenti disponibili non sembrano sufficientemente pertinenti alla query.
                              </p>
                            </div>
                          ) : (
                            <>
                              {docsToShow.map((doc, index) => {
                                const rank = doc.rank || (index + 1);
                                const isBest = rank === 1;
                                
                                return (
                                  <div
                                    key={doc.document_id || index}
                                    className={`p-6 rounded-lg shadow-md transition-all hover:shadow-lg ${
                                      isBest
                                        ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500'
                                        : 'bg-white border-l-4 border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between mb-4">
                                      <div className="flex items-start flex-1 min-w-0">
                                        <div
                                          className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mr-4 flex-shrink-0 ${
                                            rank === 1
                                              ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white shadow-lg'
                                              : rank === 2
                                              ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white shadow-md'
                                              : rank === 3
                                              ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-md'
                                              : 'bg-gray-200 text-gray-600'
                                          }`}
                                        >
                                          {rank}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <button
                                            type="button"
                                            onClick={() => handleViewDocument(doc.document_id, doc.document_name)}
                                            className="text-xl font-bold text-gray-900 hover:text-blue-600 transition text-left group inline-flex items-center"
                                          >
                                            <span className="break-all">{doc.document_name}</span>
                                            <Eye className="w-5 h-5 inline ml-2 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
                                          </button>
                                          <div className="text-sm text-gray-600 mt-1">
                                            Cartella dell'elemento: {doc.folder_path == '/' ? 'Tutti i documenti' : doc.folder_path}
                                          </div>
                                          {doc.anonymized && doc.has_original && (
                                            <div className="text-sm mt-1">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); openOriginalForDocument(doc.document_id, doc.original_filename); }}
                                                className="text-blue-600 hover:underline inline-flex items-center"
                                                title="Apri il file originale (non anonimizzato)"
                                              >
                                                <FileText className="w-4 h-4 mr-1" />
                                                File originale: {doc.original_filename || 'scarica'}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {isBest && (
                                        <span className="inline-flex items-center px-3 py-1 ml-4 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 flex-shrink-0 whitespace-nowrap">
                                          🏆 Risultato migliore
                                        </span>
                                      )}
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                      <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                        <div className="text-2xl font-bold text-green-600">
                                          {(doc.score * 100).toFixed(1)}%
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Punteggio Finale</div>
                                      </div>
                                      <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                        <div className="text-xl font-bold text-blue-600">
                                          {(doc.avg_similarity * 100).toFixed(1)}%
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Similarità Media</div>
                                      </div>
                                      <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                        <div className="text-xl font-bold text-purple-600">
                                          {(doc.max_similarity * 100).toFixed(1)}%
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Picco Massimo</div>
                                      </div>
                                      <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                        <div className="text-xl font-bold text-orange-600">{doc.chunk_count}</div>
                                        <div className="text-xs text-gray-500 mt-1">Sezioni</div>
                                      </div>
                                    </div>

                                    {doc.top_chunks && doc.top_chunks.length > 0 && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-gray-700 mb-2 flex items-center">
                                          <FileText className="w-4 h-4 mr-1" />
                                          Sezioni più rilevanti:
                                        </h5>
                                        <div className="space-y-2">
                                          {doc.top_chunks.map((chunk, chunkIdx) => (
                                            <div key={chunkIdx} className="p-3 bg-gray-50 rounded text-sm border border-gray-200">
                                              <div className="flex items-center justify-between mb-1">
                                                <span className="text-gray-600 font-medium">
                                                  Sezione {chunk.chunk_index + 1}
                                                </span>
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                                  {(chunk.similarity * 100).toFixed(1)}%
                                                </span>
                                              </div>
                                              <div className="text-gray-800">{chunk.content.substring(0, 150)}...</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    <div className="mb-4">
                                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                        <span>Rilevanza complessiva</span>
                                        <span className="font-medium">{(doc.score * 100).toFixed(1)}%</span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                          className={`h-2 rounded-full transition-all ${
                                            doc.score > 0.7
                                              ? 'bg-green-500'
                                              : doc.score > 0.5
                                              ? 'bg-blue-500'
                                              : 'bg-gray-400'
                                          }`}
                                          style={{ width: `${doc.score * 100}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {/* Pulsante per visualizzare tutti i risultati rimanenti */}
                              {!showAllResults && remainingCount > 0 && (
                                <div className="flex justify-center mt-6">
                                  <button
                                    onClick={() => setShowAllResults(true)}
                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
                                  >
                                    <ChevronDown className="w-5 h-5" />
                                    <span>Visualizza tutti gli altri {remainingCount} risultati</span>
                                    <ChevronDown className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                              
                              {/* Pulsante per nascondere i risultati extra */}
                              {showAllResults && remainingCount > 0 && (
                                <div className="flex justify-center mt-6">
                                  <button
                                    onClick={() => {
                                      setShowAllResults(false);
                                      // Scroll verso l'alto per vedere i primi risultati
                                      const resultsSection = document.querySelector('.space-y-4');
                                      if (resultsSection) {
                                        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }
                                    }}
                                    className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
                                  >
                                    <ChevronUp className="w-5 h-5" />
                                    <span>Mostra solo i primi 5 risultati</span>
                                    <ChevronUp className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  ) : (
                    <div className="p-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-gray-400 shadow-md">
                      <div className="flex items-start">
                        <AlertCircle className="w-8 h-8 text-gray-500 mr-4 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-700 text-xl mb-3">
                            Risultati poco rilevanti
                          </h4>
                          <p className="text-gray-600 text-lg mb-4">
                            Non ci sono informazioni specifiche riguardanti la ricerca nei documenti caricati.
                          </p>
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-600 mb-2">
                              <strong>Miglior match trovato:</strong> {(results[0].bestDocument.score * 100).toFixed(1)}% 
                              <span className="text-xs text-gray-500 ml-2">
                                (soglia minima: {(MIN_RELEVANCE_THRESHOLD * 100).toFixed(0)}%)
                              </span>
                            </p>
                            <p className="text-sm text-gray-500">
                              <strong>Suggerimento:</strong> Prova a:
                            </p>
                            <ul className="text-sm text-gray-500 ml-6 mt-2 space-y-1">
                              <li>• Riformulare la domanda con termini diversi</li>
                              <li>• Caricare documenti più pertinenti all'argomento</li>
                              <li>• Verificare che i documenti siano stati processati correttamente</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
          </div>
        )}
        {/* ====================================================== */}

        {/* Tab content - History */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Header con statistiche e controlli */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <History className="w-6 h-6 mr-2 text-purple-600" />
                    Storico Query RAG
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Tutte le query eseguite con le relative risposte e documenti utilizzati
                  </p>
                </div>
                
                {queryHistory.length > 0 && (
                  <button
                    onClick={handleClearAllHistory}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition flex items-center text-sm font-medium"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina Tutto
                  </button>
                )}
              </div>

              {/* Barra di ricerca */}
              {queryHistory.length > 0 && (
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={historySearchTerm}
                      onChange={(e) => setHistorySearchTerm(e.target.value)}
                      placeholder="Cerca nelle query o risposte..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  {historySearchTerm && (
                    <button
                      onClick={() => setHistorySearchTerm('')}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Lista storico */}
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <span className="ml-3 text-gray-600">Caricamento storico...</span>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {queryHistory.length === 0 
                    ? 'Nessuna query eseguita' 
                    : 'Nessuna query trovata con i criteri di ricerca'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredHistory.map((q) => (
                  <div key={q.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                    {/* Header della query */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 transition bg-gradient-to-r from-purple-50 to-blue-50"
                      onClick={() => setExpandedQuery(expandedQuery === q.id ? null : q.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-5 h-5 text-purple-600" />
                            <span className="font-semibold text-gray-900 text-lg">{q.query_text}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(q.created_at).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {/*q.model_used && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                {q.model_used}
                              </span>
                            )*/}
                            {q.relevant_chunks && q.relevant_chunks.length > 0 && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {q.relevant_chunks.length} {q.relevant_chunks.length === 1 ? 'documento' : 'documenti'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteHistory(q.id);
                            }}
                            className="p-2 hover:bg-red-100 rounded-lg transition"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-500 transition-transform ${
                              expandedQuery === q.id ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contenuto espanso */}
                    {expandedQuery === q.id && (
                      <div className="p-6 border-t border-gray-200 space-y-6">
                        {/* Risposta AI */}
                        <div className="p-5 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-l-4 border-green-500 shadow-sm">
                          <h4 className="font-bold text-green-700 text-lg mb-3 flex items-center">
                            <Brain className="w-5 h-5 mr-2" />
                            Risposta AI
                          </h4>
                          <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                              {formatTextWithBold(q.response) || 'Nessuna risposta disponibile'}
                            </p>
                          </div>
                        </div>

                        {/* Documenti utilizzati */}
                        {q.relevant_chunks && q.relevant_chunks.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="font-bold text-gray-800 text-lg flex items-center">
                              <Database className="w-5 h-5 mr-2 text-blue-600" />
                              Documenti Utilizzati ({q.relevant_chunks.length})
                            </h4>
                            
                            {/* Raggruppa i chunks per documento */}
                            {(() => {
                              // Raggruppa chunks per document_id
                              const docGroups = {};
                              q.relevant_chunks.forEach(chunk => {
                                if (!docGroups[chunk.document_id]) {
                                  docGroups[chunk.document_id] = {
                                    document_id: chunk.document_id,
                                    document_name: chunk.document_name,
                                    folder_path: chunk.folder_path || '/',
                                    has_original: !!chunk.has_original,
                                    anonymized: !!chunk.anonymized,
                                    original_filename: chunk.original_filename || null,
                                    chunks: [],
                                    maxSimilarity: 0,
                                    avgSimilarity: 0
                                  };
                                }
                                docGroups[chunk.document_id].chunks.push(chunk);
                                docGroups[chunk.document_id].maxSimilarity = Math.max(
                                  docGroups[chunk.document_id].maxSimilarity,
                                  chunk.similarity
                                );
                              });

                              // Calcola similarità media per ogni documento
                              Object.values(docGroups).forEach(group => {
                                const sum = group.chunks.reduce((acc, c) => acc + c.similarity, 0);
                                group.avgSimilarity = sum / group.chunks.length;
                              });

                              // Ordina per similarità media decrescente
                              const sortedGroups = Object.values(docGroups).sort(
                                (a, b) => b.avgSimilarity - a.avgSimilarity
                              );

                              // Filtra per similarità media > MIN_RELEVANCE_THRESHOLD
                              const filteredGroups = sortedGroups.filter(
                                group => group.avgSimilarity >= MIN_RELEVANCE_THRESHOLD
                              );

                              // 👇 NUOVA LOGICA: Determina se mostrare tutti i documenti per questa query
                              const showAllForThisQuery = expandedHistoryDocs[q.id] || false;
                              const docsToShow = showAllForThisQuery ? filteredGroups : filteredGroups.slice(0, 5);
                              const remainingCount = filteredGroups.length - 5;

                              return (
                                <>
                                  {/* Rendering documenti */}
                                  {docsToShow.map((group, index) => {
                                    const isBest = index === 0 && filteredGroups.length > 0;
                                    const rank = index + 1;

                                    return (
                                      <div
                                        key={group.document_id}
                                        className={`p-5 rounded-lg shadow-md transition-all hover:shadow-lg ${
                                          isBest
                                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400'
                                            : 'bg-white border border-gray-200'
                                        }`}
                                      >
                                        {/* Header documento */}
                                        <div className="flex items-start justify-between mb-4">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex-shrink-0">
                                                #{rank}
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleViewDocument(group.document_id, group.document_name)}
                                                className="group flex items-center gap-2 text-left min-w-0"
                                              >
                                                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                                <span className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition break-all">
                                                  {group.document_name}
                                                </span>
                                                <Eye className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
                                              </button>
                                            </div>
                                            <div className="text-sm mt-1 ml-10 text-gray-500">
                                              Cartella: {group.folder_path === '/' ? 'Tutti i documenti' : group.folder_path}
                                            </div>
                                            {group.anonymized && group.has_original && (
                                              <div className="text-sm mt-1 ml-10">
                                                <button
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); openOriginalForDocument(group.document_id, group.original_filename); }}
                                                  className="text-blue-600 hover:underline inline-flex items-center"
                                                  title="Apri il file originale (non anonimizzato)"
                                                >
                                                  <FileText className="w-4 h-4 mr-1" />
                                                  File originale: {group.original_filename || 'scarica'}
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          {isBest && (
                                            <span className="inline-flex items-center px-3 py-1 ml-4 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 flex-shrink-0 whitespace-nowrap">
                                              🏆 Risultato migliore
                                            </span>
                                          )}
                                        </div>

                                        {/* Statistiche del documento */}
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                          <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                            <div className="text-xl font-bold text-green-600">
                                              {(group.avgSimilarity * 100).toFixed(1)}%
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">Similarità Media</div>
                                          </div>
                                          <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                            <div className="text-xl font-bold text-purple-600">
                                              {(group.maxSimilarity * 100).toFixed(1)}%
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">Picco Massimo</div>
                                          </div>
                                          <div className="text-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                            <div className="text-xl font-bold text-orange-600">
                                              {group.chunks.length}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                              {group.chunks.length === 1 ? 'Sezione' : 'Sezioni'}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Barra di rilevanza */}
                                        <div className="mb-3">
                                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                            <span>Rilevanza complessiva</span>
                                            <span className="font-medium">{(group.avgSimilarity * 100).toFixed(1)}%</span>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                              className={`h-2 rounded-full transition-all ${
                                                group.avgSimilarity > 0.7
                                                  ? 'bg-green-500'
                                                  : group.avgSimilarity > 0.5
                                                  ? 'bg-blue-500'
                                                  : 'bg-gray-400'
                                              }`}
                                              style={{ width: `${group.avgSimilarity * 100}%` }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* 👇 NUOVO: Pulsante per visualizzare tutti i risultati rimanenti */}
                                  {!showAllForThisQuery && remainingCount > 0 && (
                                    <div className="flex justify-center mt-6">
                                      <button
                                        onClick={() => setExpandedHistoryDocs(prev => ({ ...prev, [q.id]: true }))}
                                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
                                      >
                                        <ChevronDown className="w-5 h-5" />
                                        <span>Visualizza tutti gli altri {remainingCount} risultati</span>
                                        <ChevronDown className="w-5 h-5" />
                                      </button>
                                    </div>
                                  )}

                                  {/* 👇 NUOVO: Pulsante per nascondere i risultati */}
                                  {showAllForThisQuery && remainingCount > 0 && (
                                    <div className="flex justify-center mt-6">
                                      <button
                                        onClick={() => setExpandedHistoryDocs(prev => ({ ...prev, [q.id]: false }))}
                                        className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow hover:bg-gray-200 flex items-center space-x-2"
                                      >
                                        <ChevronUp className="w-5 h-5" />
                                        <span>Mostra solo i primi 5 risultati</span>
                                        <ChevronUp className="w-5 h-5" />
                                      </button>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {/* Messaggio se non ci sono documenti */}
                        {(!q.relevant_chunks || q.relevant_chunks.length === 0) && (
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                            <AlertCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-gray-600 text-sm">
                              Nessun documento utilizzato per questa query
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* ====================================================== */}

        {/* Tab Gestione Aziende - SOLO per superuser */}
        {activeTab === 'companies' && user?.is_superuser && (
          <CompanyManagement />
        )}
        {/* ====================================================== */}

        {/* Tab Gestione Utenti - SOLO per superuser */}
        {activeTab === 'users' && user?.is_superuser && (
          <UserManagement />
        )}
        {/* ====================================================== */}

        {/* Tab Abbonamenti - SOLO per superuser */}
        {activeTab === 'subscriptions' && user?.is_superuser && (
          <SubscriptionManagement />
        )}
        {/* ====================================================== */}

        {/* Tab Backfill Originali - SOLO per superuser */}
        {activeTab === 'backfill_originals' && user?.is_superuser && (
          <BackfillOriginals
            onShowSuccess={showSuccess}
            onShowError={showError}
          />
        )}
        {/* ====================================================== */}
      </main>
      <Footer />
    </div>
  );
}